import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { InviteAssessorForm } from "./InviteAssessorForm";
import { useInvitations } from "../hooks/useInvitations";
import { RoleType, MAX_INVITES_PER_ROLE } from "@promo/shared";
import { Badge } from "@/components/ui/badge";

interface InviteAssessorDialogProps {
  packetId: string;
  defaultOpen?: boolean;
}

export function InviteAssessorDialog({ packetId, defaultOpen = false }: InviteAssessorDialogProps) {
  const [open, setOpen] = useState(defaultOpen);
  const { data: existingInvitations = [] } = useInvitations(packetId, RoleType.ASSESSOR);
  const invitedCount = existingInvitations.length;
  const minInvites = 3;
  const maxInvites = MAX_INVITES_PER_ROLE[RoleType.ASSESSOR];
  let helperText = '';
  
  if (invitedCount === 0) {
    helperText = `You can invite up to ${maxInvites} assessors. You'll need exactly ${minInvites} or ${maxInvites} to start assessments.`;
  } else if (invitedCount < minInvites) {
    helperText = `You have ${invitedCount} assessor${invitedCount > 1 ? 's' : ''}. Invite ${minInvites - invitedCount} more to reach ${minInvites}, or continue up to ${maxInvites}.`;
  } else if (invitedCount === minInvites) {
    helperText = `You have ${minInvites} assessors (ready to start). You can invite ${maxInvites - invitedCount} more if needed.`;
  } else if (invitedCount === maxInvites) {
    helperText = `You have ${maxInvites} assessors (ready to start). Maximum reached.`;
  } else {
    helperText = `You have ${invitedCount} assessors. Invite ${minInvites - invitedCount} more to reach ${minInvites}, or ${maxInvites - invitedCount} more to reach ${maxInvites}.`;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button disabled={invitedCount >= maxInvites} title={invitedCount >= maxInvites ? `Maximum of ${maxInvites} invites reached.` : undefined}>
          Invite Assessor
        </Button>
      </DialogTrigger>
      <DialogContent forceMount className="sm:max-w-[425px] bg-background border border-input shadow-lg">
        <DialogHeader>
          <DialogTitle className="flex flex-row items-center justify-between pr-6">
            <div>Invite assessors</div>
            <Badge variant="secondary">
              {existingInvitations.length} of {MAX_INVITES_PER_ROLE[RoleType.ASSESSOR]}
            </Badge>
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="mt-1">
          {helperText}
        </DialogDescription>
        <InviteAssessorForm
          packetId={packetId}
          onSuccess={() => setOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
} 